// The camera's life, as one state machine with no framework in it.
//
// Ported from CaptureCamera.jsx (start, resume, flip, the watchdogs, late
// adoption, the retune, the downgrades, interruptions), behaviour unchanged.
// Everything it touches comes in through createCameraSession's deps, so the
// races that cost real phones (a late getUserMedia, a stalled 4K start, a
// tap racing a pending prompt) can be tested with a fake clock.
//
// States: idle (nothing open; needsTap says a tap is wanted), requesting
// (getUserMedia asked, or the permission being resolved), opening (a stream is
// attached, no frame yet), live, retuning (same track changing size),
// flipping (also a slow-phone reopen), interrupted, denied, failed, stopped.

import { openRearCamera, preferContinuousFocus, retuneTrack, trackSummary, SCAN_QUALITY, PHOTO_QUALITY } from "./cameraStream.js";

/** A started camera must have a frame playing within this, or the page offers a button. */
export const CAMERA_LIVE_MS = 6000;
/** No picture this long after a start: the camera area turns into a tap target. */
export const CAMERA_SOFT_MS = 2500;
/** A phone that cannot keep up with a 4K camera while scanning moves to 1080p once. */
export const SLOW_CHECK_MS = 3000;
export const SLOW_FPS = 15;
export const SLOW_LOOK_MS = 80;
export const LOWRES_KEY = "cap-cam-lowres";
const PLAY_RACE_MS = 2500;
const FIRST_FRAME_MS = 5000;
const BAIL_MS = 1200;
const PROMPT_RECHECK_MS = 2000;
const TAP_WAIT_MS = 1500;
const POLL_MS = 1500;
const RESUME_FRAME_MS = 2500;
const SETTLE_FRAME_MS = 1500;

export const CAMERA_STATES = ["idle", "requesting", "opening", "live", "retuning", "flipping", "interrupted", "denied", "failed", "stopped"];
const WATCHED = ["requesting", "opening", "live", "retuning", "flipping"];
const WAITING = ["idle", "requesting", "opening", "live", "retuning"];
const HAS_STREAM = ["opening", "live", "retuning", "flipping"];

const firstTrack = (s) => s?.getVideoTracks?.()[0];

export function createCameraSession({ mediaDevices, clock, storage, mark = () => {}, span, doc, video, permissions, brave, warm, info, releaseOnHide = false } = {}) {
  const noop = () => {};
  const spanOf =
    span ||
    ((name) => {
      const t0 = clock.now();
      let done = false;
      return (extra) => {
        if (done) return null;
        done = true;
        const rec = { ms: Math.round(clock.now() - t0), ...extra };
        mark(name, rec);
        return rec;
      };
    });

  let lowRes = false;
  try { lowRes = storage?.getItem?.(LOWRES_KEY) === "1"; } catch { /* best effort */ }
  const saveLowRes = () => {
    lowRes = true;
    try { storage?.setItem?.(LOWRES_KEY, "1"); } catch { /* best effort */ }
  };

  // --- state -------------------------------------------------------------
  let snap = Object.freeze({
    state: "idle", gen: 0, facing: "environment", quality: "scan", lowRes, needsTap: false, tapToPlay: false,
    live: false, perm: "", pending: 0, askedUnfocused: false, canFlip: false, torchOk: false, error: null,
    noCam: false, resolving: false, asked: false, snap: null, step: "scanning", streamId: 0,
    attached: false, started: false, busy: false, opened: false,
  });
  const listeners = new Set();
  const set = (patch) => {
    let changed = false;
    for (const k of Object.keys(patch)) if (!Object.is(snap[k], patch[k])) { changed = true; break; }
    if (!changed) return;
    snap = Object.freeze({ ...snap, ...patch });
    for (const fn of [...listeners]) fn();
  };

  let stream = null;
  let gen = 0;
  let facing = "environment";
  let pendingN = 0;
  let gumFocus = true;
  let autoStart = false;
  let liveFlag = false;
  let busy = false; // a flip or a slow-phone reopen is running
  let stopped = false;
  let resolving = false;
  let streamSeq = 0;
  let tuneChain = Promise.resolve();
  let pollTimer = null;
  let permWatch = null;
  let docOn = false;
  let lastOpen = "start"; // what opened the current stream: start | resume | reopen | flip
  let released = false; // the camera was let go while the page was hidden (releaseOnHide)
  const timers = new Set();

  const later = (fn, ms) => {
    const id = clock.setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id);
    return id;
  };
  const wait = (ms) => new Promise((r) => later(r, ms));
  const track = () => firstTrack(stream);
  const streamIsLive = () => track()?.readyState === "live";
  const step = () => snap.step;

  const stopPoll = () => {
    if (pollTimer != null) clock.clearTimeout(pollTimer);
    pollTimer = null;
  };
  const stopStream = () => {
    stopPoll();
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    set({ attached: false });
  };
  /** Where a camera that is simply there rests. */
  const rest = () => (stream ? (liveFlag || snap.live ? "live" : "opening") : "idle");
  const go = (state, patch = {}) => set({ ...patch, state });

  // --- watching the track --------------------------------------------------
  const startPoll = () => {
    stopPoll();
    const tick = () => {
      pollTimer = clock.setTimeout(tick, POLL_MS);
      if (busy) return;
      const t = track();
      if (!t || t.readyState === "ended") interrupt("poll");
    };
    pollTimer = clock.setTimeout(tick, POLL_MS);
  };

  /** A dead track on the scan step is not a dead end: the camera area asks for a tap. */
  function interrupt(via) {
    mark("camera_interrupted", { via });
    if (step() === "scanning") {
      stopStream();
      liveFlag = false;
      set({ live: false, snap: null, needsTap: true, state: "idle" });
      return;
    }
    stopPoll();
    go("interrupted");
  }

  // --- getUserMedia, attach, play, first frame -------------------------------
  /** Returns the adopted stream, or null when the answer was released. */
  async function attach(attempt) {
    const endCam = spanOf("camera_start");
    warm?.();
    liveFlag = false;
    pendingN += 1;
    gumFocus = doc?.hasFocus?.() ?? true;
    set({ pending: pendingN, askedUnfocused: !gumFocus });
    const askedAt = clock.now();
    let got;
    try {
      got = await openRearCamera(SCAN_QUALITY, facing, mediaDevices);
    } catch (err) {
      pendingN = Math.max(0, pendingN - 1);
      set({ pending: pendingN });
      endCam({ ok: false, why: err?.name });
      throw err;
    }
    pendingN = Math.max(0, pendingN - 1);
    set({ pending: pendingN });

    // getUserMedia can answer long after the watchdog gave up. That late camera
    // is still the one the page is waiting for, so the first answer wins; it is
    // released only when another stream is live, a flip is under way, the page
    // moved past scanning, or the session was stopped. The generation token
    // alone never drops a stream (481ddb3f).
    const stale = attempt != null && attempt !== gen;
    const waiting = WAITING.includes(snap.state) && step() !== "other";
    if (stopped || video.ready?.() === false || streamIsLive() || (stale && (!waiting || busy))) {
      got.getTracks().forEach((t) => t.stop());
      return null;
    }
    const waited = clock.now() - askedAt;
    if (stale || waited > CAMERA_LIVE_MS) mark("camera_late", { ms: Math.round(waited), stale: stale ? 1 : 0, focus: doc?.hasFocus?.() ? 1 : 0 });

    stream = got;
    streamSeq += 1;
    video.attach(got);
    set({ tapToPlay: false, streamId: streamSeq, attached: false });
    if (!busy) go("opening");
    startPoll();

    const playT0 = clock.now();
    let playP;
    try { playP = Promise.resolve(video.play()); } catch (err) { playP = Promise.reject(err); }
    await Promise.race([
      playP.then(
        () => mark("camera_play", { ok: 1, ms: Math.round(clock.now() - playT0), via: "auto" }),
        (err) => {
          mark("camera_play", { ok: 0, via: "auto", why: err?.name });
          // Autoplay without a gesture is refused on some browsers: the stream is
          // fine but the video sits black. Offer a tap, which is a gesture.
          if (err?.name === "NotAllowedError" && stream === got) {
            mark("camera_fallback", { reason: "play_blocked" });
            set({ tapToPlay: true });
          }
        },
      ),
      // play() can also never settle; the watchdog covers the rest.
      wait(PLAY_RACE_MS),
    ]);

    const t = firstTrack(got);
    void video.firstFrame(FIRST_FRAME_MS).then((ok) => {
      // A stream the watchdog or a flip has already replaced says nothing about the camera on screen now.
      if (stream !== got) return;
      if (ok) {
        liveFlag = true;
        // A camera opened again after being let go (releaseOnHide) replaces the held frame.
        set({ live: true, needsTap: false, ...(snap.snap === "resume" ? { snap: null } : {}) });
        if (!video.paused()) set({ tapToPlay: false });
        if (snap.state === "opening") go("live");
        scheduleTune();
      }
      const st = t?.getSettings?.() || {};
      endCam({ ok: true, w: st.width, h: st.height, fps: st.frameRate });
      try {
        const c = t?.getCapabilities?.() || {};
        mark("camera_caps", {
          maxW: c.width?.max, maxH: c.height?.max, maxFps: c.frameRate?.max, torch: Boolean(c.torch),
          focus: (c.focusMode || []).join("+"), zoom: c.zoom?.max, facing: st.facingMode || "", label: (t?.label || "").slice(0, 40),
        });
      } catch { /* capabilities are optional */ }
    });
    // A new stream starts with the torch off; Flip only where a second camera is listed.
    set({ torchOk: Boolean(t?.getCapabilities?.().torch) });
    try {
      Promise.resolve(mediaDevices?.enumerateDevices?.())
        .then((d) => { if (d) set({ canFlip: d.filter((x) => x.kind === "videoinput").length > 1 }); })
        .catch(noop);
    } catch { /* optional */ }
    if (t) {
      t.addEventListener("ended", () => {
        // Only the current stream's track may interrupt; a flip stops the old one.
        if (busy || stream !== got) return;
        interrupt("ended");
      });
      // Asked for again after play(): getCapabilities is not reliably populated before it.
      void preferContinuousFocus(t);
      (info || ((...a) => console.info?.(...a)))("[capture] camera granted", trackSummary(t));
    }
    if (stream === got) set({ attached: true, opened: true });
    return got;
  }

  // --- the watchdogs ------------------------------------------------------------
  /** Open but not one frame decoded (readyState 0): reopen once at 1080p by itself. */
  function downgradeIfStalled() {
    const t = track();
    if (lowRes || t?.readyState !== "live" || !video.hasSource() || video.readyState() > 0) return false;
    const st = t.getSettings?.() || {};
    mark("camera_downgrade", { from: `${st.width}x${st.height}`, to: `${SCAN_QUALITY.width}x${SCAN_QUALITY.height}` });
    saveLowRes();
    set({ lowRes: true });
    stopStream();
    doStart();
    return true;
  }

  function watchLive(attempt) {
    // Soft: no picture 2.5 s in, the whole camera area becomes a tap target. The start carries on.
    later(() => {
      if (attempt !== gen || liveFlag) return;
      if (pendingN && snap.perm === "prompt") return;
      if (downgradeIfStalled()) return;
      set({ needsTap: true });
    }, CAMERA_SOFT_MS);
    const hard = () => {
      if (attempt !== gen || liveFlag) return;
      // A typed or preset code is locked while the camera waits for a tap (idle): the old page phase
      // was "locked" there, which the watchdog counted.
      if (!WATCHED.includes(snap.state) && !(snap.state === "idle" && step() === "locked")) return;
      // The browser's own prompt is still up: waiting on the person, not on the camera.
      if (pendingN && snap.perm === "prompt") {
        later(hard, PROMPT_RECHECK_MS);
        return;
      }
      const reason = pendingN ? "gum_pending" : "timeout";
      const t = track();
      mark("camera_fallback", {
        reason, rs: video.readyState(), paused: video.paused() ? 1 : 0, vw: video.el?.()?.videoWidth, track: t?.readyState || "-",
        muted: t?.muted ? 1 : 0, focus: doc?.hasFocus?.() ? 1 : 0, vis: doc?.visibilityState,
      });
      if (downgradeIfStalled()) return;
      // Open but the video never started (Brave Android): keep the stream; the tap plays it.
      if (t?.readyState === "live" && video.hasSource()) {
        set({ needsTap: true });
        return;
      }
      // A request still unanswered is left running: its answer is adopted whenever it comes.
      if (!pendingN) gen += 1;
      autoStart = false;
      stopStream();
      liveFlag = false;
      set({ gen, tapToPlay: false, live: false, needsTap: true, state: "idle" });
    };
    later(hard, CAMERA_LIVE_MS);
  }

  // --- start, resume -------------------------------------------------------------
  async function doStart() {
    set({ started: true });
    ensurePermWatch();
    lastOpen = "start";
    stopped = false;
    registerDoc();
    resolving = false;
    set({ error: null, noCam: false, resolving: false, live: false, state: "requesting" });
    liveFlag = false;
    const attempt = ++gen;
    set({ gen });
    watchLive(attempt);

    if (!mediaDevices?.getUserMedia) {
      set({ error: Object.freeze({ name: "NotSupportedError", message: "This browser does not expose a camera to web pages." }), noCam: true, state: "failed" });
      return;
    }
    try {
      const got = await attach(attempt);
      if (!got) {
        // The answer was released because a live stream is already on screen: that is the camera.
        if (snap.state === "requesting" && stream) keepExisting();
        return;
      }
    } catch (err) {
      // The watchdog already moved on from this start.
      if (attempt !== gen) return;
      const name = err?.name || "";
      mark("camera_error", { name });
      if (name === "NotAllowedError" || name === "SecurityError") {
        // The camera is asked for on arrival, with no tap. A browser that only asks after a tap
        // refuses that first try: offer the button then, and call it denied only when the tap is refused too.
        if (autoStart) {
          autoStart = false;
          set({ needsTap: true, state: "idle" });
          return;
        }
        set({ error: Object.freeze({ name, message: "" }), state: "denied" });
        return;
      }
      set({
        noCam: name === "NotFoundError" || name === "OverconstrainedError",
        error: Object.freeze({ name, message: name === "NotFoundError" ? "No camera was found on this device." : "The camera could not be started. Reload and try again." }),
        state: "failed",
      });
    }
  }

  /** A start that was answered while a stream is already on screen: that stream is the camera. */
  function keepExisting() {
    const playing = !video.paused();
    liveFlag = playing;
    set({ live: playing });
    go(playing ? "live" : "opening");
  }

  /** Bring the camera back, stopping the old stream first (a dead track can come back as itself). */
  async function resume() {
    stopped = false;
    registerDoc();
    set({ started: true, error: null, needsTap: false, live: false, state: "requesting" });
    liveFlag = false;
    const attempt = ++gen;
    set({ gen });
    watchLive(attempt);
    lastOpen = "resume";
    try {
      stopStream();
      const got = await attach(attempt);
      if (!got && snap.state === "requesting" && stream) keepExisting();
    } catch {
      go("interrupted");
    }
  }

  /** The arrival flow: ask for the camera with no tap, once the permission says it may be asked. */
  function arrive() {
    ensurePermWatch();
    stopped = false;
    registerDoc();
    resolving = true;
    set({ resolving: true, started: true, state: "requesting" });
    const myGen = gen;
    // Never leave the screen blank because a query hung.
    const bail = later(() => {
      if (resolving && myGen === gen) {
        resolving = false;
        autoStart = true;
        doStart();
      }
    }, BAIL_MS);
    (async () => {
      try {
        const status = await permissions?.query?.({ name: "camera" });
        // Only act while still undecided.
        if (!resolving || myGen !== gen) return;
        if (status?.state === "denied") {
          resolving = false;
          set({ resolving: false, state: "denied" });
          return;
        }
        resolving = false;
        autoStart = status?.state !== "granted";
        doStart();
      } catch {
        // No Permissions API (Safari): ask straight away all the same.
        if (resolving && myGen === gen) {
          resolving = false;
          autoStart = true;
          doStart();
        }
      } finally {
        clock.clearTimeout(bail);
      }
    })();
  }

  function ensurePermWatch() {
    if (permWatch) return;
    permWatch = { gone: false, status: null };
    const w = permWatch;
    const sync = () => {
      const p = w.status?.state || "";
      set({ perm: p, asked: snap.asked || p === "prompt" });
      if (p === "granted" && snap.state === "denied") {
        autoStart = false;
        doStart();
      }
    };
    w.sync = sync;
    Promise.resolve(permissions?.query?.({ name: "camera" }))
      .then((st) => {
        if (w.gone || !st) return;
        w.status = st;
        sync();
        st.addEventListener?.("change", sync);
      })
      .catch(noop);
  }

  // --- tap -------------------------------------------------------------------------
  function tap() {
    if (snap.state === "interrupted") return resume();
    const hasStream = Boolean(stream && video.hasSource() && streamIsLive());
    mark("camera_tap", { via: hasStream ? "play" : "start", pending: pendingN, focus: gumFocus ? 1 : 0 });
    set({ needsTap: false });
    // Asked without focus and still unanswered: Safari answers once the page has focus, which this
    // tap gives it. A second request now would mute the first camera when it answers, so wait a beat.
    if (!hasStream && pendingN && !gumFocus) {
      later(() => {
        if (pendingN && !stream) {
          autoStart = false;
          doStart();
        }
      }, TAP_WAIT_MS);
      return undefined;
    }
    if (hasStream) {
      const mine = stream;
      const t0 = clock.now();
      let p;
      try { p = Promise.resolve(video.play()); } catch (err) { p = Promise.reject(err); }
      p.then(() => {
        mark("camera_play", { ok: 1, ms: Math.round(clock.now() - t0), via: "tap" });
        set({ tapToPlay: false });
        return video.firstFrame(FIRST_FRAME_MS);
      })
        .then((ok) => {
          if (!ok || stream !== mine) return;
          liveFlag = true;
          set({ live: true });
          if (snap.state === "opening" || snap.state === "requesting") go("live");
          scheduleTune();
        })
        .catch((err) => mark("camera_play", { ok: 0, via: "tap", why: err?.name }));
      return undefined;
    }
    autoStart = false;
    set({ tapToPlay: false });
    if (stream) stopStream();
    return doStart();
  }

  // --- flip --------------------------------------------------------------------------
  async function flip() {
    if (busy) return;
    lastOpen = "flip";
    setBusy(true);
    go("flipping");
    if (video.holdFrame?.()) set({ snap: "flip" });
    const prev = facing;
    const swap = async (f) => {
      facing = f;
      stopStream();
      const got = await attach();
      if (!got) return;
      set({ facing: f });
    };
    try {
      try {
        await swap(prev === "environment" ? "user" : "environment");
      } catch {
        // The other camera would not open; reopen the one we had.
        await swap(prev);
      }
      endBusy(false);
    } catch {
      endBusy(true);
    }
    await settleAfter();
  }

  function setBusy(b) {
    busy = b;
    set({ busy: b });
  }
  function endBusy(failed) {
    setBusy(false);
    if (failed) go("interrupted");
    else if (snap.state === "flipping") go(rest());
  }
  async function settleAfter() {
    if (!video.paused()) await video.nextFrame(SETTLE_FRAME_MS);
    set({ snap: snap.snap === "flip" ? null : snap.snap });
    scheduleTune();
  }

  /** A phone too slow for 4K while scanning reopens once at 1080p behind the held frame. */
  async function reportSlow(stats) {
    const st = typeof stats === "function" ? stats() : stats;
    const el = video.el?.();
    if (!st || !el || lowRes || busy || step() !== "scanning") return "skipped";
    if (Math.max(el.videoWidth, el.videoHeight) <= SCAN_QUALITY.width) return "skipped";
    if (!st.ticks || st.runMs < SLOW_CHECK_MS * 0.8) return "skipped";
    const fps = (st.frames * 1000) / st.runMs;
    const lookMs = st.tickMs / st.ticks;
    if (fps >= SLOW_FPS || lookMs < SLOW_LOOK_MS) return "skipped";
    // Mid-typing: not now, look again shortly.
    if (doc?.activeTag?.() === "INPUT") {
      later(() => reportSlow(stats), SLOW_CHECK_MS);
      return "deferred";
    }
    mark("camera_downgrade", {
      from: `${el.videoWidth}x${el.videoHeight}`, to: `${SCAN_QUALITY.width}x${SCAN_QUALITY.height}`,
      why: "slow", fps: Math.round(fps), lookMs: Math.round(lookMs),
    });
    saveLowRes();
    set({ lowRes: true });
    lastOpen = "reopen";
    setBusy(true); // the state stays as it was: the old code only held the frame
    if (video.holdFrame?.()) set({ snap: "flip" });
    let failed = false;
    try {
      stopStream();
      await attach();
    } catch {
      failed = true;
    }
    endBusy(failed);
    if (!video.paused()) await video.nextFrame(SETTLE_FRAME_MS);
    set({ snap: snap.snap === "flip" ? null : snap.snap });
    scheduleTune();
    return "downgraded";
  }

  // --- one camera, two qualities ----------------------------------------------------
  /** Steps run one at a time and each reads the quality when it runs. */
  function scheduleTune() {
    if (stopped || !snap.live || step() === "other") return tuneChain;
    const mine = stream;
    const t = track();
    if (!t || !video.el?.()) return tuneChain;
    tuneChain = tuneChain
      .then(async () => {
        const s = step();
        if (stream !== mine || (s !== "locked" && s !== "scanning") || busy) return;
        const photo = snap.quality === "photo" && !lowRes;
        const el = video.el();
        const from = `${el.videoWidth}x${el.videoHeight}`;
        const t0 = clock.now();
        const wasLive = snap.state === "live";
        if (wasLive) go("retuning");
        try {
          const got = await retuneTrack(t, el, photo ? PHOTO_QUALITY : SCAN_QUALITY, clock);
          if (got === undefined) return;
          mark(photo ? "camera_raise" : "camera_lower", {
            ok: got ? 1 : 0, ms: Math.round(clock.now() - t0), from, to: got ? `${got.w}x${got.h}` : `${el.videoWidth}x${el.videoHeight}`,
          });
          if (!got && photo) {
            saveLowRes();
            set({ lowRes: true });
            await retuneTrack(t, el, SCAN_QUALITY, clock);
          }
        } finally {
          if (snap.state === "retuning") go(rest());
        }
      })
      .catch(noop);
    return tuneChain;
  }

  function setQuality(q) {
    set({ quality: q === "photo" ? "photo" : "scan" });
    return scheduleTune();
  }

  // --- the page going away and coming back -------------------------------------------
  // ANDROID LETS GO WHILE HIDDEN (releaseOnHide). A page left open behind another tab or app
  // (the finished Quick Scan after Back to Shopify) kept the camera, and the next capture tab
  // got a track that never sent a frame until the phone switched apps. Android re-grants a
  // granted camera silently, so coming back opens it again; iOS would ask again, so it keeps it.
  function reclaim() {
    if (!released || stopped || step() === "other" || doc?.visibilityState !== "visible") return;
    released = false;
    mark("camera_reclaim", {});
    void resume();
  }
  function onVisibility() {
    if (stopped) return;
    if (released) {
      if (doc?.visibilityState === "visible") reclaim();
      return;
    }
    if (!HAS_STREAM.includes(snap.state)) return;
    const scanStep = step() === "scanning";
    // Leaving: keep the last frame, so coming back shows it, blurred, not a black box.
    if (doc?.visibilityState !== "visible") {
      const held = scanStep && !busy && video.holdFrame?.();
      if (releaseOnHide && !busy) {
        released = true;
        gen += 1; // a pending watchdog or late answer belongs to the stream let go
        stopStream();
        liveFlag = false;
        mark("camera_release", { step: step() });
        set({ gen, live: false, needsTap: false, tapToPlay: false, snap: held ? "resume" : null, state: "idle" });
        return;
      }
      if (held) set({ snap: "resume" });
      return;
    }
    if (busy) return;
    const t = track();
    if (!t || t.readyState === "ended") {
      interrupt("visible");
      return;
    }
    try { Promise.resolve(video.play()).catch(noop); } catch { /* best effort */ }
    if (!scanStep) return;
    // Plays again by itself; if no new frame comes, ask for a tap.
    const mine = stream;
    void video.nextFrame(RESUME_FRAME_MS).then((ok) => {
      if (stream !== mine) return;
      set({ snap: snap.snap === "resume" ? null : snap.snap });
      if (!ok) {
        liveFlag = false;
        set({ live: false, needsTap: true });
      }
    });
  }
  function registerDoc() {
    if (docOn || !doc?.addEventListener) return;
    docOn = true;
    doc.addEventListener("visibilitychange", onVisibility);
  }

  // --- the rest of the API ------------------------------------------------------------
  function start({ auto = false } = {}) {
    if (auto) return arrive();
    autoStart = false;
    return doStart();
  }

  function stop() {
    stopped = true;
    released = false;
    gen += 1;
    resolving = false;
    busy = false;
    stopStream();
    for (const id of timers) clock.clearTimeout(id);
    timers.clear();
    if (docOn) doc.removeEventListener("visibilitychange", onVisibility);
    docOn = false;
    if (permWatch) {
      permWatch.gone = true;
      permWatch.status?.removeEventListener?.("change", permWatch.sync);
      permWatch = null;
    }
    liveFlag = false;
    set({ gen, busy: false, opened: false, state: "stopped", live: false, resolving: false, snap: null, needsTap: false, tapToPlay: false });
  }

  return {
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getSnapshot: () => snap,
    getStream: () => stream,
    start,
    tap,
    flip,
    resume,
    setQuality,
    /** The tuning chain, for a shutter that waits for a raise in progress (race it against 1.5 s). */
    tuned: () => tuneChain,
    reportSlow,
    onVisibility,
    /** The component's phase, which gates late adoption and the dead-track branch: "scanning" | "locked" | "other". */
    setStep(s) {
      set({ step: s });
      if (s !== "other") scheduleTune();
      reclaim();
    },
    stop,
    /** The page could not use the camera it was given (the scanner would not start): mark it interrupted. */
    markInterrupted() {
      stopPoll();
      go("interrupted");
    },
    /**
     * The page could not use the camera it was given (the scanner would not start). The stream is
     * released. A first start shows the error panel; a resume or a reopen is just interrupted.
     */
    fail(err) {
      stopStream();
      if (lastOpen !== "start") {
        go("interrupted");
        return;
      }
      const name = err?.name || "";
      mark("camera_error", { name });
      set({ error: Object.freeze({ name, message: "The camera could not be started. Reload and try again." }), state: "failed" });
    },
    /** Logged on mount: the browser says it is Brave. */
    markBrave() {
      try {
        if (brave && typeof brave.isBrave === "function") mark("camera_brave", {});
      } catch { /* detection is best-effort */ }
    },
    trackSummary: () => trackSummary(track()),
  };
}
